<template>
  <div class="wishlist-view">
    <div class="wishlist-page-layout">
      <div
        class="mobile-view-tabs"
        role="tablist"
        aria-label="行きたいところ表示"
        :aria-busy="wishlistStore.loading"
      >
        <button
          ref="mobileListTab"
          id="wishlist-list-tab"
          type="button"
          role="tab"
          :class="['mobile-view-tab', { active: activeMobilePanel === 'list' }]"
          :aria-selected="activeMobilePanel === 'list'"
          :tabindex="activeMobilePanel === 'list' ? 0 : -1"
          aria-controls="wishlist-list-panel"
          :disabled="wishlistStore.loading"
          @click="activeMobilePanel = 'list'"
          @keydown="handleMobileTabKeydown"
        >
          <IconSvg name="list-solid-full" size="sm" />
          一覧
        </button>
        <button
          ref="mobileMapTab"
          id="wishlist-map-tab"
          type="button"
          role="tab"
          :class="['mobile-view-tab', { active: activeMobilePanel === 'map' }]"
          :aria-selected="activeMobilePanel === 'map'"
          :tabindex="activeMobilePanel === 'map' ? 0 : -1"
          aria-controls="wishlist-map-panel"
          :disabled="wishlistStore.loading"
          @click="activeMobilePanel = 'map'"
          @keydown="handleMobileTabKeydown"
        >
          <IconSvg name="map-solid-full" size="sm" />
          マップ
        </button>
      </div>

      <main
        id="wishlist-list-panel"
        class="wishlist-left-pane"
        :class="{ 'mobile-panel-inactive': activeMobilePanel !== 'list' }"
        :role="isMobileViewport ? 'tabpanel' : undefined"
        :aria-labelledby="isMobileViewport ? 'wishlist-list-tab' : undefined"
      >
    <!-- ヘッダーバー -->
    <div class="wishlist-header">
      <div class="header-left">
        <h2 class="section-title">
          <IconSvg name="map-solid-full" size="sm" />
          行きたいところリスト
        </h2>
        <span class="item-count" role="status" aria-live="polite" aria-atomic="true">{{ filteredItems.length }}件</span>
      </div>
      <div class="header-right">
        <button
          v-if="canWrite"
          class="ai-recommend-button"
          :class="{ disabled: isTrialMode }"
          :disabled="isTrialMode"
          :title="isTrialMode ? 'AIおすすめはログイン後に利用できます' : ''"
          @click="handleRecommendationButtonClick"
        >
          <IconSvg name="plane-solid-full" size="sm" />
          {{
            recommendationButtonLabel
          }}
        </button>
        <p v-if="isTrialMode" class="trial-ai-note">
          AIおすすめはログイン後に利用できます。
        </p>
        <!-- ステータスフィルター -->
        <div class="filter-tabs">
          <button
            v-for="tab in statusTabs"
            :key="tab.value"
            :class="['filter-tab', { active: filters.status === tab.value }]"
            @click="filters.status = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
    </div>

    <button
      v-if="canWrite"
      class="mobile-ai-recommend-button"
      :class="{ disabled: isTrialMode }"
      :disabled="isTrialMode"
      :title="isTrialMode ? 'AIおすすめはログイン後に利用できます' : ''"
      @click="handleRecommendationButtonClick"
    >
      <IconSvg name="plane-solid-full" size="sm" />
      {{
        recommendationButtonLabel
      }}
    </button>

    <section
      v-if="canWrite && isRecommendationPanelOpen"
      class="recommendation-panel"
    >
      <div class="recommendation-heading">
        <div>
          <span class="recommendation-label">AI RECOMMEND</span>
          <h3>グループの好みに合わせたおすすめスポット</h3>
          <p>気になる場所にみんなで投票しよう</p>
        </div>
        <div class="recommendation-theme">
          <input
            v-model.trim="recommendationTheme"
            type="text"
            maxlength="120"
            placeholder="テーマ例：歴史、絶景、子ども向け"
          />
          <button
            v-if="!wishlistStore.recommendationLoading"
            :disabled="wishlistStore.recommendationLoading"
            @click="loadRecommendations"
          >
            再提案
          </button>
          <button
            v-else
            type="button"
            class="recommendation-stop-button"
            aria-label="AIおすすめ取得を停止"
            title="AIおすすめ取得を停止"
            @click="stopRecommendations"
          >
            ■
          </button>
          <button
            type="button"
            class="recommendation-close-button"
            aria-label="おすすめエリアを閉じる"
            @click="closeRecommendationPanel"
          >
            ×
          </button>
        </div>
      </div>

      <div v-if="wishlistStore.recommendationLoading" class="loading">
        <div class="spinner"></div>
        おすすめスポットを探しています...
      </div>
      <div v-else-if="recommendations.length === 0" class="recommendation-empty">
        テーマを入れて、行きたい候補を探してみましょう。
      </div>
      <div v-else class="recommendation-grid">
        <article
          v-for="spot in recommendations"
          :key="spot.title"
          class="recommendation-card"
        >
          <span class="category-badge">{{ spot.category }}</span>
          <h4>{{ spot.title }}</h4>
          <p>{{ spot.description }}</p>
          <span v-if="spot.address" class="recommendation-address">
            {{ spot.address }}
          </span>
          <span v-if="spot.estimatedCost" class="recommendation-cost">
            目安 ¥{{ Number(spot.estimatedCost).toLocaleString() }}
          </span>
          <button
            class="recommendation-add-button"
            :class="{ added: isRecommendationAdded(spot) }"
            :disabled="
              isRecommendationAdded(spot) || addingSpotTitles.has(spot.title)
            "
            @click="addRecommendation(spot)"
          >
            {{
              isRecommendationAdded(spot)
                ? "追加済み"
                : addingSpotTitles.has(spot.title)
                  ? "追加中..."
                  : "行きたいところに追加"
            }}
          </button>
        </article>
      </div>
    </section>

    <button
      ref="mobileFilterTrigger"
      class="mobile-filter-button"
      aria-controls="wishlist-filter-dialog"
      :aria-expanded="filterSidebarOpen"
      @click="openFilterSidebar"
    >
      <IconSvg name="list-solid-full" size="sm" />
      フィルター
      <span v-if="hasFilters" class="filter-active-dot"></span>
    </button>

    <div
      v-if="filterSidebarOpen"
      class="filter-overlay"
      @click="closeFilterSidebar"
    ></div>
    <aside v-if="filterSidebarOpen" id="wishlist-filter-dialog" ref="mobileFilterDialog" class="mobile-filter-sidebar is-open" role="dialog" aria-modal="true" aria-labelledby="wishlist-filter-title" @keydown.esc.prevent="closeFilterSidebar">
      <div class="filter-sidebar-header">
        <strong id="wishlist-filter-title">絞り込み</strong>
        <button ref="mobileFilterClose" @click="closeFilterSidebar" aria-label="絞り込みを閉じる">×</button>
      </div>
      <div class="mobile-filter-content">
        <div class="filter-section">
          <label class="filter-label">ステータス</label>
          <select v-model="filters.status" class="filter-select">
            <option
              v-for="tab in statusTabs"
              :key="tab.value"
              :value="tab.value"
            >
              {{ tab.label }}
            </option>
          </select>
        </div>
        <div v-if="availableTags.length" class="filter-section tag-filter-section">
          <span class="filter-label">タグ（複数選択可）</span>
          <div class="tag-filter-list">
            <button v-for="tag in availableTags" :key="tag" type="button" :class="['tag-filter-chip', { active: filters.tags.includes(tag) }]" :aria-pressed="filters.tags.includes(tag)" @click="toggleTagFilter(tag)">{{ tag }}</button>
          </div>
          <button v-if="filters.tags.length" type="button" class="clear-tags-btn" @click="clearTagFilters">タグのみ解除</button>
        </div>
        <div class="filter-section">
          <label class="filter-label">カテゴリ</label>
          <select v-model="filters.category" class="filter-select">
            <option value="">すべて</option>
            <option v-for="cat in categories" :key="cat" :value="cat">
              {{ cat }}
            </option>
          </select>
        </div>
        <div class="filter-section">
          <label class="filter-label">並び替え</label>
          <select v-model="filters.sortBy" class="filter-select">
            <option value="wishNo">追加順</option>
            <option value="votes">いいね数順</option>
          </select>
        </div>
        <button v-if="hasFilters" class="reset-btn" @click="resetFilters">
          リセット
        </button>
      </div>
    </aside>

    <!-- 追加フィルターバー -->
    <div class="filter-bar">
      <!-- カテゴリ -->
      <div class="filter-section">
        <label class="filter-label">カテゴリ：</label>
        <select v-model="filters.category" class="filter-select">
          <option value="">すべて</option>
          <option v-for="cat in categories" :key="cat" :value="cat">
            {{ cat }}
          </option>
        </select>
      </div>

      <div v-if="availableTags.length" class="filter-section tag-filter-section">
        <span class="filter-label">タグ：</span>
        <div class="tag-filter-list">
          <button v-for="tag in availableTags" :key="tag" type="button" :class="['tag-filter-chip', { active: filters.tags.includes(tag) }]" :aria-pressed="filters.tags.includes(tag)" @click="toggleTagFilter(tag)">{{ tag }}</button>
        </div>
        <button v-if="filters.tags.length" type="button" class="clear-tags-btn" @click="clearTagFilters">タグのみ解除</button>
      </div>

      <!-- 並び替え -->
      <div class="filter-section">
        <label class="filter-label">並び替え：</label>
        <select v-model="filters.sortBy" class="filter-select">
          <option value="wishNo">追加順</option>
          <option value="votes">いいね数順</option>
        </select>
      </div>

      <!-- リセット -->
      <button
        v-if="hasFilters"
        class="reset-btn"
        @click="resetFilters"
        title="フィルターをリセット"
      >
        ↺ リセット
      </button>
    </div>

    <!-- ローディング -->
    <div v-if="wishlistStore.loading" class="loading">
      <div class="spinner"></div>
      読み込み中...
    </div>

    <!-- 空の状態 -->
    <div
      v-else-if="filteredItems.length === 0 && allItems.length === 0"
      class="empty"
    >
      <div class="empty-icon">
        <IconSvg name="map-solid-full" size="xl" />
      </div>
      <p>みんなの「行きたいところ」を集めて、旅行先検討に活かそう！</p>
      <button v-if="canWrite" class="create-btn" @click="openCreateModal">
        最初の候補を追加
      </button>
    </div>

    <!-- フィルター後に空 -->
    <div v-else-if="filteredItems.length === 0" class="empty">
      <p>フィルター条件に一致する候補はありません</p>
      <button class="create-btn" @click="resetFilters">
        フィルターをリセット
      </button>
    </div>

    <!-- リスト -->
    <div v-else class="wish-list">
      <div
        v-for="item in numberedFilteredItems"
        :key="item.WISH_NO"
        class="wish-card"
        :class="[
          `status-${statusClass(item.STATUS_CDV)}`,
          { 'menu-open': openWishMenuId === item.WISH_NO },
        ]"
        @click="handleWishCardClick($event, item)"
      >
        <div class="wish-pin-number">
          {{ item.MAP_PIN_NUMBER }}
        </div>
        <!-- カード左: メイン情報 -->
        <div class="card-main">
          <div class="card-row-top">
            <span v-if="item.CATEGORY_NMV" class="category-badge">
              {{ item.CATEGORY_NMV }}
            </span>
            <span
              :class="[
                'status-badge',
                `status-badge-${statusClass(item.STATUS_CDV)}`,
              ]"
            >
              {{ statusLabel(item.STATUS_CDV) }}
            </span>
          </div>

          <h3 class="place-name">{{ item.PLACE_NMV }}</h3>

          <div v-if="item.TAGS.length" class="wish-tag-list" :aria-label="`${item.PLACE_NMV}のタグ`">
            <span v-for="tag in item.TAGS" :key="tag" class="wish-tag">{{ tag }}</span>
          </div>

          <p v-if="item.ADDRESS_NMV" class="place-meta">
            <IconSvg name="map-solid-full" size="xs" />
            {{ item.ADDRESS_NMV }}
          </p>

          <a
            v-if="getWishMapUrl(item)"
            :href="getWishMapUrl(item)"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Google Mapsで見る（新しいタブ）"
            class="place-url map-url"
          >
            <IconSvg name="map-solid-full" size="xs" />
            Google Mapsで見る
          </a>

          <p v-if="item.AMOUNT !== null && item.AMOUNT !== undefined" class="place-cost">
            <IconSvg name="yen-sign-solid-full" size="xs" />
            {{ formatWishAmount(item) }}
          </p>

          <p v-if="item.MEMO_TXV" class="place-memo">{{ item.MEMO_TXV }}</p>

          <a
            v-if="item.URL_NMV"
            :href="item.URL_NMV"
            target="_blank"
            rel="noopener noreferrer"
            class="place-url"
          >
            <IconSvg name="link-solid-full" size="xs" />
            参考リンク
          </a>
        </div>

        <!-- カード右: アクション -->
        <div v-if="canWrite" class="card-actions desktop-wish-actions">
          <!-- いいね！ボタン -->
          <button
            :class="['vote-btn', { voted: item.MY_VOTE }]"
            @click="handleVote(item)"
            title="行きたい！"
          >
            <span class="vote-icon">♥</span>
            <span class="vote-count">{{ item.VOTE_COUNT }}</span>
          </button>

          <!-- ステータス変更 -->
          <select
            class="status-select"
            :value="item.STATUS_CDV"
            @change="handleStatusChange(item, $event.target.value)"
          >
            <option value="検討中">検討中</option>
            <option value="決定">決定</option>
            <option value="却下">見送り</option>
          </select>

          <!-- スケジュールに追加 -->
          <button
            class="action-btn btn-schedule"
            @click="openScheduleFromWish(item)"
            title="スケジュールに追加"
          >
            <IconSvg name="calendar-solid-full" size="sm" />
            スケジュール追加
          </button>

          <!-- 編集 -->
          <button
            class="action-btn btn-edit"
            @click="openEditModal(item)"
            title="編集"
          >
            <IconSvg name="pen-to-square-solid-full" size="sm" />
          </button>

          <!-- 削除 -->
          <button
            class="action-btn btn-delete"
            @click="handleDelete(item)"
            title="削除"
          >
            <IconSvg name="trash-solid-full" size="sm" />
          </button>
        </div>

        <div v-if="canWrite" class="mobile-wish-actions" @click.stop>
          <button
            :class="['vote-btn', { voted: item.MY_VOTE }]"
            @click="handleVote(item)"
          >
            <span class="vote-icon">♥</span>
            <span class="vote-count">{{ item.VOTE_COUNT }}</span>
          </button>
          <div class="wish-kebab-wrap">
            <button
              class="wish-kebab-button"
              @click="toggleWishMenu(item.WISH_NO)"
              aria-label="候補メニュー"
            >
              ⋮
            </button>
            <div
              v-if="openWishMenuId === item.WISH_NO"
              class="wish-action-menu"
            >
              <label class="wish-status-label">
                ステータス
                <select
                  class="status-select"
                  :value="item.STATUS_CDV"
                  @change="
                    handleStatusChange(item, $event.target.value);
                    openWishMenuId = null;
                  "
                >
                  <option value="検討中">検討中</option>
                  <option value="決定">決定</option>
                  <option value="却下">見送り</option>
                </select>
              </label>
              <button
                class="wish-action-item"
                @click="runWishAction(openScheduleFromWish, item)"
              >
                スケジュールに追加
              </button>
              <button
                class="wish-action-item"
                @click="runWishAction(openEditModal, item)"
              >
                編集
              </button>
              <button
                class="wish-action-item wish-action-delete"
                @click="runWishAction(handleDelete, item)"
              >
                削除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

      </main>

      <aside
        v-if="!wishlistStore.loading"
        id="wishlist-map-panel"
        class="wishlist-map-pane"
        :class="{ 'mobile-panel-inactive': activeMobilePanel !== 'map' }"
        :role="isMobileViewport ? 'tabpanel' : undefined"
        :aria-labelledby="isMobileViewport ? 'wishlist-map-tab' : undefined"
      >
        <section class="wishlist-side-map-section">
          <div class="wishlist-map-header">
            <h3 class="wishlist-map-title">
              <IconSvg name="map-solid-full" size="sm" />
              行きたいところマップ
            </h3>
            <span class="wishlist-map-count" aria-hidden="true">
              {{ wishlistItemsWithCoordinates.length }}件
            </span>
          </div>
          <div class="wishlist-map-body">
            <MapComponent
              :items="wishlistItemsWithCoordinates"
              title-field="PLACE_NMV"
              address-field="ADDRESS_NMV"
              category-field="CATEGORY_NMV"
              item-key-field="WISH_NO"
              :focused-item-key="focusedWishlistMapItemKey"
              :center="wishlistMapCenter"
              :zoom="wishlistMapZoom"
            />
            <p
              v-if="wishlistItemsWithCoordinates.length === 0"
              class="wishlist-map-empty"
            >
              地図に表示できる場所がありません。行きたいところを追加・編集して場所候補を選択し、住所と緯度経度が保存されるとピンが表示されます。
            </p>
          </div>
        </section>
      </aside>
    </div>

    <!-- フローティング追加ボタン -->
    <button
      v-if="canWrite"
      class="floating-btn"
      @click="openCreateModal"
      title="候補を追加"
    >
      <span class="floating-btn-icon">
        <IconSvg name="plus-solid-full" size="md" />
      </span>
    </button>

    <!-- 候補追加/編集モーダル -->
    <CreateWishlistModal
      v-if="canWrite"
      :is-open="isWishModalOpen"
      :shiori-id="shioriId"
      :edit-mode="wishEditMode"
      :wishlist-data="editingWish"
      @close="closeWishModal"
      @create="handleWishCreate"
      @update="handleWishUpdate"
    />

    <!-- スケジュール作成モーダル（候補から） -->
    <CreateScheduleModal
      v-if="canWrite"
      v-model="isScheduleModalOpen"
      :shiori-id="shioriId"
      :prefill-data="scheduleFromWishData"
      :initial-date="scheduleInitialDate"
      @create="handleScheduleCreate"
    />
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import {
  cancelWishlistRecommendations,
  useWishlistStore,
} from "@/store/wishlist-store";
import { useShioriStore } from "@/store/shiori-store";
import IconSvg from "@/components/IconSvg.vue";
import CreateWishlistModal from "@/components/CreateWishlistModal.vue";
import CreateScheduleModal from "@/components/CreateScheduleModal.vue";
import MapComponent from "@/components/MapComponent.vue";

export default {
  name: "WishlistView",
  components: { IconSvg, CreateWishlistModal, CreateScheduleModal, MapComponent },
  props: {
    canWrite: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const route = useRoute();
    const wishlistStore = useWishlistStore();
    const shioriStore = useShioriStore();

    const shioriId = computed(() => route.params.id);
    const isTrialMode = computed(() => route.name === "Trial");

    const isWishModalOpen = ref(false);
    const wishEditMode = ref(false);
    const editingWish = ref(null);

    const isScheduleModalOpen = ref(false);
    const scheduleFromWishData = ref(null);
    const filterSidebarOpen = ref(false);
    const mobileFilterTrigger = ref(null);
    const mobileFilterDialog = ref(null);
    const mobileFilterClose = ref(null);
    const openWishMenuId = ref(null);
    const recommendationTheme = ref("");
    const addingSpotTitles = ref(new Set());
    const isRecommendationPanelOpen = ref(false);
    const focusedWishlistMapItemKey = ref(null);
    const activeMobilePanel = ref("list");
    const mobileListTab = ref(null);
    const mobileMapTab = ref(null);
    const isMobileViewport = ref(false);
    let mobileViewportQuery = null;

    const filters = ref({
      status: "すべて",
      category: "",
      sortBy: "wishNo",
      tags: [],
    });

    const statusTabs = [
      { label: "すべて", value: "すべて" },
      { label: "検討中", value: "検討中" },
      { label: "決定", value: "決定" },
      { label: "見送り", value: "却下" },
    ];

    const allItems = computed(() =>
      wishlistStore.getItemsBySioriId(shioriId.value),
    );
    const recommendations = computed(
      () => wishlistStore.recommendations[shioriId.value] || [],
    );
    const currentShiori = computed(() =>
      shioriStore.shiori.find((item) => item.SHIORI_NOV === shioriId.value),
    );
    const scheduleInitialDate = computed(
      () => currentShiori.value?.TRIP_FROM || null,
    );
    const recommendationButtonLabel = computed(() => {
      if (isTrialMode.value) return "AIおすすめはログイン後";
      if (wishlistStore.recommendationLoading) return "AIが提案中...";
      if (isRecommendationPanelOpen.value) return "閉じる";
      if (recommendations.value.length > 0) return "AIおすすめを開く";
      return "AIにおすすめを聞く";
    });

    const categories = computed(() => {
      const set = new Set();
      allItems.value.forEach((i) => {
        if (i.CATEGORY_NMV) set.add(i.CATEGORY_NMV);
      });
      return Array.from(set).sort();
    });

    const availableTags = computed(() => {
      const tags = new Set();
      allItems.value.forEach((item) => {
        (Array.isArray(item.TAGS) ? item.TAGS : []).forEach((tag) => tags.add(tag));
      });
      return Array.from(tags).sort((a, b) => a.localeCompare(b, "ja"));
    });

    const hasFilters = computed(
      () =>
        filters.value.status !== "すべて" ||
        filters.value.category !== "" ||
        filters.value.sortBy !== "wishNo" ||
        filters.value.tags.length > 0,
    );

    const filteredItems = computed(() => {
      let list = allItems.value;

      if (filters.value.status !== "すべて") {
        list = list.filter((i) => i.STATUS_CDV === filters.value.status);
      }
      if (filters.value.category) {
        list = list.filter((i) => i.CATEGORY_NMV === filters.value.category);
      }
      if (filters.value.tags.length) {
        list = list.filter((item) =>
          filters.value.tags.some((tag) => item.TAGS.includes(tag)),
        );
      }
      if (filters.value.sortBy === "votes") {
        list = [...list].sort(
          (a, b) => (b.VOTE_COUNT || 0) - (a.VOTE_COUNT || 0),
        );
      }
      return list;
    });

    const numberedFilteredItems = computed(() =>
      filteredItems.value.map((item, index) => ({
        ...item,
        MAP_PIN_NUMBER: index + 1,
      })),
    );

    const getCoordinateValue = (item, uppercaseKey, lowercaseKey) => {
      const value = item?.[uppercaseKey] ?? item?.[lowercaseKey];
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        return null;
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };

    const hasCoordinates = (item) => {
      const lat = getCoordinateValue(item, "LATITUDE", "latitude");
      const lng = getCoordinateValue(item, "LONGITUDE", "longitude");
      return (
        lat !== null &&
        lng !== null &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      );
    };

    const wishlistItemsWithCoordinates = computed(() =>
      numberedFilteredItems.value
        .filter(
          (item) => hasCoordinates(item) && Boolean(item?.ADDRESS_NMV?.trim()),
        )
        .map((item) => ({
          ...item,
          googleMapsUrl: getWishMapUrl(item),
        })),
    );

    const wishlistMapCenter = computed(() => {
      if (wishlistItemsWithCoordinates.value.length === 0) {
        return [35.6762, 139.6503];
      }
      const avgLat =
        wishlistItemsWithCoordinates.value.reduce(
          (sum, item) =>
            sum + getCoordinateValue(item, "LATITUDE", "latitude"),
          0,
        ) / wishlistItemsWithCoordinates.value.length;
      const avgLng =
        wishlistItemsWithCoordinates.value.reduce(
          (sum, item) =>
            sum + getCoordinateValue(item, "LONGITUDE", "longitude"),
          0,
        ) / wishlistItemsWithCoordinates.value.length;
      return [avgLat, avgLng];
    });

    const wishlistMapZoom = computed(() =>
      wishlistItemsWithCoordinates.value.length > 1 ? 12 : 14,
    );

    const resetFilters = () => {
      filters.value = { status: "すべて", category: "", sortBy: "wishNo", tags: [] };
    };

    const toggleTagFilter = (tag) => {
      filters.value.tags = filters.value.tags.includes(tag)
        ? filters.value.tags.filter((item) => item !== tag)
        : [...filters.value.tags, tag];
    };
    const clearTagFilters = () => { filters.value.tags = []; };

    const openFilterSidebar = async () => {
      filterSidebarOpen.value = true;
      await nextTick();
      mobileFilterClose.value?.focus();
    };

    const closeFilterSidebar = async () => {
      filterSidebarOpen.value = false;
      await nextTick();
      mobileFilterTrigger.value?.focus();
    };

    const trapFilterFocus = (event) => {
      if (!filterSidebarOpen.value || event.key !== "Tab") return;
      const focusable = mobileFilterDialog.value?.querySelectorAll(
        "button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])",
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const statusClass = (status) => {
      const map = { 検討中: "pending", 決定: "decided", 却下: "rejected" };
      return map[status] || "pending";
    };

    const statusLabel = (status) => (status === "却下" ? "見送り" : status);

    const getWishMapUrl = (item) => {
      const placeName = String(item?.PLACE_NMV ?? "").trim();
      const address = String(item?.ADDRESS_NMV ?? "").trim();
      const query = placeName || address;
      return query
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            query,
          )}`
        : null;
    };

    const focusWishOnMap = async (item) => {
      if (!hasCoordinates(item)) return;
      focusedWishlistMapItemKey.value = null;
      await nextTick();
      focusedWishlistMapItemKey.value = item.WISH_NO;
    };

    const handleWishCardClick = (event, item) => {
      const interactiveSelector =
        "a, button, input, select, textarea, [role='button'], .card-actions, .mobile-wish-actions";
      if (event.target.closest(interactiveSelector)) return;
      focusWishOnMap(item);
    };

    const openCreateModal = () => {
      if (!props.canWrite) return;
      wishEditMode.value = false;
      editingWish.value = null;
      isWishModalOpen.value = true;
    };

    const openEditModal = (item) => {
      if (!props.canWrite) return;
      wishEditMode.value = true;
      editingWish.value = item;
      isWishModalOpen.value = true;
    };

    const closeWishModal = () => {
      isWishModalOpen.value = false;
    };

    const handleWishCreate = async () => {
      if (!props.canWrite) return;
      await wishlistStore.fetchWishlist(shioriId.value);
    };

    const handleWishUpdate = async () => {
      if (!props.canWrite) return;
      await wishlistStore.fetchWishlist(shioriId.value);
    };

    const handleDelete = async (item) => {
      if (!props.canWrite) return;
      if (!confirm(`「${item.PLACE_NMV}」を削除しますか？`)) return;
      try {
        await wishlistStore.deleteWishlist(shioriId.value, item.WISH_NO);
      } catch {
        alert("削除に失敗しました");
      }
    };

    const handleVote = async (item) => {
      if (!props.canWrite) return;
      try {
        await wishlistStore.toggleVote(shioriId.value, item.WISH_NO);
      } catch (err) {
        if (err.response?.status === 400) {
          alert("投票するにはこのしおりのメンバーである必要があります");
        }
      }
    };

    const handleStatusChange = async (item, newStatus) => {
      if (!props.canWrite) return;
      if (newStatus === item.STATUS_CDV) return;
      try {
        await wishlistStore.updateWishlist(shioriId.value, item.WISH_NO, {
          statusCdv: newStatus,
        });
      } catch {
        alert("ステータスの更新に失敗しました");
      }
    };

    const openScheduleFromWish = (item) => {
      if (!props.canWrite) return;
      scheduleFromWishData.value = {
        wishNo: item.WISH_NO,
        statusCdv: item.STATUS_CDV,
        title: item.PLACE_NMV,
        address: item.ADDRESS_NMV || "",
        url: item.URL_NMV || "",
        remarks: item.MEMO_TXV || "",
        cost: item.AMOUNT !== null ? Number(item.AMOUNT) : null,
        currency: item.CURRENCY || "JPY",
        latitude: getCoordinateValue(item, "LATITUDE", "latitude"),
        longitude: getCoordinateValue(item, "LONGITUDE", "longitude"),
      };
      isScheduleModalOpen.value = true;
    };

    const handleScheduleCreate = async () => {
      if (!props.canWrite) return;
      const wishNo = scheduleFromWishData.value?.wishNo;
      const statusCdv = scheduleFromWishData.value?.statusCdv;
      isScheduleModalOpen.value = false;
      if (wishNo && statusCdv !== "決定") {
        try {
          await wishlistStore.updateWishlist(shioriId.value, wishNo, {
            statusCdv: "決定",
          });
        } catch {
          alert("スケジュール追加後のステータス更新に失敗しました");
        }
      }
      scheduleFromWishData.value = null;
    };

    const formatWishAmount = (item) => {
      const amount = Number(item.AMOUNT || 0).toLocaleString();
      return `¥${amount}`;
    };

    const toggleWishMenu = (wishNo) => {
      if (!props.canWrite) return;
      openWishMenuId.value =
        openWishMenuId.value === wishNo ? null : wishNo;
    };

    const runWishAction = (action, item) => {
      if (!props.canWrite) return;
      openWishMenuId.value = null;
      action(item);
    };

    const closeWishMenu = () => {
      openWishMenuId.value = null;
    };

    const loadRecommendations = async () => {
      if (!props.canWrite) return;
      isRecommendationPanelOpen.value = true;
      try {
        const result = await wishlistStore.fetchRecommendations(
          shioriId.value,
          recommendationTheme.value,
        );
        if (result?.canceled) return;
      } catch {
        alert(wishlistStore.error || "おすすめ候補の取得に失敗しました");
      }
    };

    const handleRecommendationButtonClick = () => {
      if (!props.canWrite) return;
      if (isTrialMode.value) return;
      if (wishlistStore.recommendationLoading) {
        isRecommendationPanelOpen.value = true;
        return;
      }
      if (isRecommendationPanelOpen.value) {
        closeRecommendationPanel();
        return;
      }
      if (recommendations.value.length > 0) {
        isRecommendationPanelOpen.value = true;
        return;
      }
      loadRecommendations();
    };

    const stopRecommendations = () => {
      cancelWishlistRecommendations(wishlistStore);
    };

    const closeRecommendationPanel = () => {
      if (wishlistStore.recommendationLoading) {
        cancelWishlistRecommendations(wishlistStore);
      }
      isRecommendationPanelOpen.value = false;
    };

    const isRecommendationAdded = (spot) =>
      allItems.value.some(
        (item) =>
          item.PLACE_NMV?.trim().toLowerCase() ===
          spot.title?.trim().toLowerCase(),
      );

    const addRecommendation = async (spot) => {
      if (!props.canWrite) return;
      if (isRecommendationAdded(spot)) return;
      addingSpotTitles.value = new Set([
        ...addingSpotTitles.value,
        spot.title,
      ]);
      try {
        await wishlistStore.createWishlist({
          shioriId: shioriId.value,
          placeNmv: spot.title,
          categoryNmv: spot.category || "観光",
          memoTxv: spot.description,
          urlNmv: spot.url || null,
          addressNmv: spot.address || null,
          latitude: spot.latitude ?? null,
          longitude: spot.longitude ?? null,
          amount: spot.estimatedCost ?? null,
          currency: spot.currency || "JPY",
        });
      } catch {
        alert(wishlistStore.error || "候補の追加に失敗しました");
      } finally {
        const next = new Set(addingSpotTitles.value);
        next.delete(spot.title);
        addingSpotTitles.value = next;
      }
    };

    const syncMobileViewport = (event) => {
      isMobileViewport.value = event.matches;
    };

    const selectMobilePanelAndFocus = async (panel) => {
      if (wishlistStore.loading) return;
      activeMobilePanel.value = panel;
      await nextTick();
      (panel === "list" ? mobileListTab.value : mobileMapTab.value)?.focus();
    };

    const handleMobileTabKeydown = (event) => {
      if (event.key === "Home") {
        event.preventDefault();
        selectMobilePanelAndFocus("list");
      } else if (event.key === "End") {
        event.preventDefault();
        selectMobilePanelAndFocus("map");
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        selectMobilePanelAndFocus(
          activeMobilePanel.value === "list" ? "map" : "list",
        );
      }
    };

    onMounted(async () => {
      mobileViewportQuery = window.matchMedia("(max-width: 640px)");
      isMobileViewport.value = mobileViewportQuery.matches;
      mobileViewportQuery.addEventListener("change", syncMobileViewport);
      document.addEventListener("click", closeWishMenu);
      document.addEventListener("keydown", trapFilterFocus);
      if (shioriId.value) {
        await shioriStore.fetchShiori();
        await wishlistStore.fetchWishlist(shioriId.value);
      }
    });

    onUnmounted(() => {
      mobileViewportQuery?.removeEventListener("change", syncMobileViewport);
      document.removeEventListener("click", closeWishMenu);
      document.removeEventListener("keydown", trapFilterFocus);
    });

    return {
      shioriId,
      isTrialMode,
      wishlistStore,
      allItems,
      filteredItems,
      numberedFilteredItems,
      wishlistItemsWithCoordinates,
      wishlistMapCenter,
      wishlistMapZoom,
      focusedWishlistMapItemKey,
      activeMobilePanel,
      mobileListTab,
      mobileMapTab,
      handleMobileTabKeydown,
      isMobileViewport,
      filters,
      statusTabs,
      categories,
      availableTags,
      hasFilters,
      resetFilters,
      toggleTagFilter,
      clearTagFilters,
      statusClass,
      statusLabel,
      getWishMapUrl,
      handleWishCardClick,
      isWishModalOpen,
      wishEditMode,
      editingWish,
      isScheduleModalOpen,
      scheduleFromWishData,
      filterSidebarOpen,
      mobileFilterTrigger,
      mobileFilterDialog,
      mobileFilterClose,
      openFilterSidebar,
      closeFilterSidebar,
      openWishMenuId,
      recommendations,
      recommendationTheme,
      addingSpotTitles,
      isRecommendationPanelOpen,
      scheduleInitialDate,
      recommendationButtonLabel,
      openCreateModal,
      openEditModal,
      closeWishModal,
      handleWishCreate,
      handleWishUpdate,
      handleDelete,
      handleVote,
      handleStatusChange,
      openScheduleFromWish,
      handleScheduleCreate,
      formatWishAmount,
      toggleWishMenu,
      runWishAction,
      loadRecommendations,
      handleRecommendationButtonClick,
      stopRecommendations,
      closeRecommendationPanel,
      isRecommendationAdded,
      addRecommendation,
    };
  },
};
</script>

<style scoped>
.wishlist-view {
  position: relative;
  padding-bottom: 100px;
}

.wishlist-page-layout {
  display: flex;
  width: 100%;
  max-width: 1480px;
  flex-direction: column;
  gap: 1rem;
  margin: 0 auto;
}

.wishlist-left-pane,
.wishlist-map-pane {
  min-width: 0;
}

.mobile-view-tabs {
  display: none;
}

.wishlist-side-map-section {
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.wishlist-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-border-light);
}

.wishlist-map-title {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 0.5rem;
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 700;
}

.wishlist-map-count {
  display: inline-flex;
  align-items: center;
  height: 1.75rem;
  padding: 0 0.65rem;
  color: var(--primary-hover);
  background: rgba(111, 145, 94, 0.14);
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.wishlist-map-body {
  position: relative;
  min-height: 360px;
}

.wishlist-map-body :deep(.map-container),
.wishlist-map-body :deep(.map) {
  min-height: 360px;
}

.wishlist-map-empty {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  left: 1rem;
  z-index: 2;
  margin: 0;
  padding: 0.75rem 0.9rem;
  color: var(--color-text-muted);
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  font-size: 0.82rem;
  line-height: 1.6;
}

.wishlist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.ai-recommend-button {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.9rem;
  color: white;
  background: linear-gradient(135deg, #a1bf81, #c6dab0);
  border: 0;
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
  font-weight: 700;
  min-height: 44px;
  min-width: 44px;
}

.ai-recommend-button:disabled {
  cursor: wait;
  opacity: 0.7;
}

.ai-recommend-button.disabled,
.mobile-ai-recommend-button.disabled {
  cursor: not-allowed;
  background: #e5e7eb;
  color: var(--color-text-muted);
  box-shadow: none;
}

.trial-ai-note {
  flex-basis: 100%;
  margin: -0.35rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.78rem;
  line-height: 1.5;
}

.recommendation-panel {
  margin-bottom: 1.25rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #f5faef, #ffffff);
  border: 1px solid #d4e5c7;
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
}

.recommendation-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.recommendation-heading h3,
.recommendation-heading p {
  margin: 0;
}

.recommendation-heading p {
  font-size: 0.82rem;
}

.recommendation-label {
  color: var(--primary-hover);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
}

.recommendation-theme {
  display: flex;
  gap: 0.5rem;
}

.recommendation-theme input {
  min-width: 250px;
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font: inherit;
  font-size: 0.82rem;
}

.recommendation-theme button {
  padding: 0.55rem 0.8rem;
  color: var(--primary-hover);
  background: var(--color-surface);
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  min-height: 44px;
  min-width: 44px;
}

.recommendation-theme .recommendation-stop-button {
  width: 44px;
  height: 44px;
  min-width: 44px;
  padding: 0;
  color: #5f6368;
  background: #eef0f2;
  border-color: #c9cdd2;
  font-weight: 800;
}

.recommendation-theme .recommendation-stop-button:hover {
  color: #3c4043;
  background: #dfe2e5;
}

.recommendation-close-button {
  width: 44px;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  color: var(--color-text-muted);
}

.recommendation-empty {
  padding: 1.5rem;
  color: var(--color-text-muted);
  background: var(--color-surface);
  border: 1px dashed var(--color-border);
  border-radius: 8px;
  font-size: 0.85rem;
  text-align: center;
}

.recommendation-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.8rem;
}

.recommendation-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: 12px;
}

.recommendation-card h4 {
  margin: 0.55rem 0 0.35rem;
  font-size: 0.98rem;
}

.recommendation-card p {
  margin: 0;
  font-size: 0.8rem;
}

.recommendation-address,
.recommendation-cost {
  margin-top: 0.45rem;
  color: var(--color-text-muted);
  font-size: 0.74rem;
}

.recommendation-add-button {
  width: 100%;
  margin-top: auto;
  padding: 0.55rem 0.7rem;
  color: white;
  background: var(--primary-hover);
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 700;
}

.recommendation-add-button.added {
  color: var(--color-text-muted);
  background: var(--color-cool-bg);
}

.recommendation-add-button:disabled {
  cursor: default;
  opacity: 0.8;
}

.mobile-filter-button,
.mobile-ai-recommend-button,
.mobile-filter-sidebar,
.filter-overlay,
.mobile-wish-actions {
  display: none;
}

/* フィルターバー */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}

.tag-filter-section {
  min-width: 0;
  flex: 1 1 100%;
}

.tag-filter-list,
.wish-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-width: 0;
}

.tag-filter-chip,
.wish-tag {
  max-width: 100%;
  overflow-wrap: anywhere;
  border-radius: 999px;
  font-size: 0.75rem;
}

.tag-filter-chip {
  padding: 0.3rem 0.625rem;
  border: 1px solid #9eb897;
  background: #f7faf4;
  color: #365432;
  cursor: pointer;
}

.tag-filter-chip.active {
  border-color: #365432;
  background: #365432;
  color: #fff;
}

.tag-filter-chip:focus-visible,
.clear-tags-btn:focus-visible {
  outline: 3px solid rgba(54, 84, 50, 0.25);
  outline-offset: 2px;
}

.clear-tags-btn {
  padding: 0.25rem 0.5rem;
  border: 0;
  background: transparent;
  color: #526b4d;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
}

.wish-tag-list {
  margin: 0.25rem 0 0.5rem;
}

.wish-tag {
  padding: 0.2rem 0.5rem;
  background: #e7f0df;
  color: #294525;
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-label {
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: 500;
  white-space: nowrap;
}

.filter-select {
  padding: 0.5rem 0.875rem;
  font-size: 0.9rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-base);
}
.filter-select:hover {
  border-color: var(--primary-color);
}
.filter-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.reset-btn {
  padding: 0.5rem 0.875rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  color: var(--color-text-muted);
  background: var(--color-surface);
  cursor: pointer;
  transition: all var(--transition-base);
  white-space: nowrap;
}
.reset-btn:hover {
  border-color: var(--primary-color);
  color: var(--color-text);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
}

.item-count {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  background: var(--color-background);
  padding: 0.2rem 0.6rem;
  border-radius: 99px;
}

/* フィルタータブ */
.filter-tabs {
  display: flex;
  gap: 0.375rem;
}

.filter-tab {
  padding: 0.375rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 99px;
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  background: var(--color-surface);
  cursor: pointer;
  transition: all 0.15s;
}

.filter-tab.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
  font-weight: 500;
}

/* カード */
.wish-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.wish-card {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  background: var(--color-surface);
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #d1d5db;
  transition: box-shadow 0.2s;
}

.wish-card.menu-open {
  z-index: 1200;
}

.wish-pin-number {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  color: #ffffff;
  background: #b4cf9e;
  border: 2px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(180, 207, 158, 0.28);
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1;
}

.wish-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.wish-card.status-pending {
  border-left-color: #f59e0b;
}
.wish-card.status-decided {
  border-left-color: #10b981;
}
.wish-card.status-rejected {
  border-left-color: #d1d5db;
  opacity: 0.65;
}

.card-main {
  flex: 1;
  min-width: 0;
}

.card-row-top {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.category-badge {
  font-size: 0.75rem;
  background: #eff6ff;
  color: #3b82f6;
  padding: 0.15rem 0.5rem;
  border-radius: 99px;
  font-weight: 500;
}

.status-badge {
  font-size: 0.75rem;
  padding: 0.15rem 0.5rem;
  border-radius: 99px;
  font-weight: 500;
}
.status-badge-pending {
  background: #fef3c7;
  color: #92400e;
}
.status-badge-decided {
  background: #d1fae5;
  color: #065f46;
}
.status-badge-rejected {
  background: var(--color-background);
  color: var(--color-text-muted);
}

.place-name {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 0.375rem;
}

.place-meta {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0 0 0.25rem;
}

.place-cost {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin: 0.25rem 0 0;
  color: #166534;
  font-size: 0.875rem;
  font-weight: 700;
}

.place-memo {
  font-size: 0.875rem;
  color: var(--color-text);
  margin: 0.375rem 0 0;
  white-space: pre-wrap;
}

.place-url {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8125rem;
  color: #3b82f6;
  text-decoration: none;
  margin-top: 0.375rem;
}
.place-url:hover {
  text-decoration: underline;
}

.place-url.map-url {
  min-width: 44px;
  min-height: 44px;
  color: #46623a;
}

.place-url.map-url:hover,
.place-url.map-url:focus-visible {
  color: #2f4726;
  text-decoration: underline;
  text-decoration-thickness: 2px;
}

/* アクション列 */
.card-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
  flex-shrink: 0;
}

/* いいね！ボタン */
.vote-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid #fca5a5;
  border-radius: 99px;
  background: var(--color-surface);
  color: var(--color-danger);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.15s;
}
.vote-btn.voted {
  background: #ef4444;
  color: white;
  border-color: var(--color-danger);
}
.vote-btn:hover {
  transform: scale(1.05);
}

.vote-icon {
  font-size: 1rem;
}

/* ステータス変更 */
.status-select {
  font-size: 0.8125rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: var(--color-text);
  background: var(--color-surface);
  cursor: pointer;
}

/* アクションボタン共通 */
.action-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}

.btn-schedule {
  background: #eff6ff;
  color: #2563eb;
}
.btn-schedule:hover {
  background: #dbeafe;
}

.btn-edit {
  background: var(--color-background);
  color: var(--color-text);
  padding: 0.375rem 0.625rem;
}
.btn-edit:hover {
  background: #e5e7eb;
}

.btn-delete {
  background: #fee2e2;
  color: var(--color-danger);
  padding: 0.375rem 0.625rem;
}
.btn-delete:hover {
  background: #fecaca;
}

/* ローディング */
.loading {
  text-align: center;
  padding: 3rem;
  color: var(--color-text-muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 空の状態 */
.empty {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--color-text-muted);
}
.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.4;
}
.empty p {
  margin-bottom: 1.5rem;
  font-size: 1rem;
}
.create-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

/* フローティングボタン */
.floating-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-hover) 100%
  );
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.4),
    0 8px 24px rgba(180, 207, 158, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  z-index: 100;
}
.floating-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(180, 207, 158, 0.5),
    0 12px 32px rgba(180, 207, 158, 0.3);
}
.floating-btn:active {
  transform: scale(1.05);
}

.floating-btn-icon img {
  filter: brightness(0);
}

/* レスポンシブ */
@media (min-width: 1024px) {
  .wishlist-page-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(460px, 1fr);
    align-items: start;
    gap: 1.5rem;
  }

  .wishlist-map-pane {
    position: sticky;
    top: 1.5rem;
    height: calc(100vh - 3rem);
  }

  .wishlist-side-map-section {
    display: flex;
    height: 100%;
    flex-direction: column;
  }

  .wishlist-map-body {
    flex: 1;
    min-height: 0;
  }

  .wishlist-map-body :deep(.map-container),
  .wishlist-map-body :deep(.map) {
    height: 100%;
    min-height: 540px;
  }
}

@media (max-width: 640px) {
  .mobile-view-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
    padding: 0.25rem;
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-lg);
    background: var(--color-background);
  }

  .mobile-view-tab {
    display: inline-flex;
    min-height: 44px;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.625rem 0.75rem;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--color-text-muted);
    font: inherit;
    font-size: 0.875rem;
    font-weight: 700;
    cursor: pointer;
  }

  .mobile-view-tab.active {
    background: var(--color-surface);
    color: var(--primary-hover);
    box-shadow: var(--shadow-sm);
  }

  .mobile-view-tab:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .mobile-view-tab:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .wishlist-left-pane.mobile-panel-inactive,
  .wishlist-map-pane.mobile-panel-inactive {
    display: none;
  }

  .wishlist-header {
    gap: 0.5rem;
  }

  .header-right,
  .filter-bar {
    display: none;
  }

  .mobile-ai-recommend-button {
    display: inline-flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    margin-bottom: 0.75rem;
    padding: 0.65rem;
    color: white;
    background: linear-gradient(135deg, #a1bf81, #c6dab0);
    border: 0;
    border-radius: var(--radius-md);
    font: inherit;
    font-size: 0.8rem;
    font-weight: 700;
    min-height: 44px;
  }

  .recommendation-panel {
    padding: 0.9rem;
  }

  .recommendation-heading {
    flex-direction: column;
  }

  .recommendation-theme {
    width: 100%;
  }

  .recommendation-theme input {
    min-width: 0;
    flex: 1;
  }

  .recommendation-grid {
    grid-template-columns: 1fr;
  }

  .mobile-filter-button {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-text);
    font-size: 0.75rem;
    font-weight: 600;
    box-shadow: var(--shadow-sm);
    min-width: 44px;
    min-height: 44px;
  }

  .filter-active-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--primary-color);
  }

  .filter-overlay {
    position: fixed;
    inset: 0;
    z-index: 200;
    display: block;
    background: rgba(0, 0, 0, 0.35);
  }

  .mobile-filter-sidebar {
    position: fixed;
    top: 0;
    left: -100%;
    z-index: 201;
    display: flex;
    flex-direction: column;
    width: min(320px, 86vw);
    height: 100dvh;
    background: var(--color-surface);
    box-shadow: var(--shadow-xl);
    transition: left 0.25s ease;
  }

  .mobile-filter-sidebar.is-open {
    left: 0;
  }

  .filter-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    background: linear-gradient(135deg, #b4cf9e, #c5dcb3);
    color: white;
  }

  .filter-sidebar-header button {
    width: 44px;
    height: 44px;
    border: none;
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.2);
    color: white;
    font-size: 1.25rem;
  }

  .mobile-filter-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    min-height: 0;
    overflow-y: auto;
  }

  .mobile-filter-content .tag-filter-chip,
  .mobile-filter-content .clear-tags-btn {
    min-width: 44px;
    min-height: 44px;
  }

  .mobile-filter-content .filter-section {
    align-items: stretch;
    flex-direction: column;
    gap: 0.375rem;
  }

  .wish-list {
    gap: 0.5rem;
  }

  .wish-card {
    position: relative;
    flex-direction: row;
    gap: 0.5rem;
    padding: 0.75rem;
    border-left-width: 3px;
    border-radius: var(--radius-md);
  }

  .card-row-top {
    gap: 0.25rem;
    margin-bottom: 0.25rem;
  }

  .category-badge,
  .status-badge {
    padding: 0.125rem 0.4rem;
    font-size: 0.6875rem;
  }

  .place-name {
    margin-bottom: 0.25rem;
    font-size: 0.9375rem;
  }

  .place-meta,
  .place-url,
  .place-memo {
    font-size: 0.75rem;
  }

  .place-memo {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .desktop-wish-actions {
    display: none;
  }

  .mobile-wish-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.375rem;
    flex-shrink: 0;
  }

  .mobile-wish-actions .vote-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
  }

  .wish-kebab-wrap {
    position: relative;
    z-index: 1201;
  }

  .wish-kebab-button {
    width: 30px;
    height: 30px;
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-text-muted);
    font-size: 1.25rem;
    line-height: 1;
  }

  .wish-action-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 1202;
    min-width: 165px;
    overflow: hidden;
    background: var(--color-surface);
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
  }

  .wish-action-item,
  .wish-status-label {
    width: 100%;
    display: block;
    padding: 0.625rem 0.75rem;
    border: none;
    background: var(--color-surface);
    color: var(--color-text);
    font-size: 0.75rem;
    text-align: left;
  }

  .wish-action-item:hover {
    background: var(--color-background);
  }

  .wish-status-label .status-select {
    width: 100%;
    margin-top: 0.375rem;
  }

  .wish-action-delete {
    color: var(--color-danger);
  }

  .floating-btn {
    bottom: 1rem;
    right: 1rem;
    width: 3.5rem;
    height: 3.5rem;
  }
}
</style>
